<script lang="ts" setup>
export interface UILeaveConfirmStepProps {
    name: string
    priority: number
    title: string
    message: string
    stayLabel: string
    leaveLabel: string
    /** Opens on top of the step showing, e.g. the signup confirm over the auth sheet. */
    interrupt?: boolean
}

const props = withDefaults(defineProps<UILeaveConfirmStepProps>(), {
    interrupt: false
})

const modelValue = defineModel<boolean>({ default: false })

const emit = defineEmits<{
    leave: []
    /** The confirm has left the sheet, e.g. so a parent can unmount once nothing it holds is showing. */
    closed: []
}>()

defineSlots<{
    /** The very top of the sheet, e.g. the DK self-assessment bar the step it covers shows. */
    top?(): any
    /** In place of an illustration, e.g. the signup confirm's emoji. */
    media?(): any
    /** Below the buttons, e.g. a switch to login on the signup confirm. */
    default?(props: { close: () => void }): any
}>()

// Leave goes out before the model turns false, so a parent that resets on close still has its pending action.
function leave(close: () => void) {
    emit('leave')
    close()
}
</script>

<template>
    <UIDialogStep
        v-model="modelValue"
        :name="props.name"
        :priority="props.priority"
        :interrupt="props.interrupt"
        :close-on-backdrop-click="false"
        @closed="emit('closed')"
    >
        <template v-if="$slots.top" #top>
            <slot name="top" />
        </template>

        <template #default="{ close }">
            <UISheetPage :title="props.title">
                <template v-if="$slots.media" #media>
                    <slot name="media" />
                </template>

                <div v-text="props.message" />

                <template #actions>
                    <UIButton
                        as="cta"
                        block
                        size="xl"
                        @click="close"
                    >
                        {{ props.stayLabel }}
                    </UIButton>

                    <UIButton
                        type="button"
                        color="secondary"
                        block
                        size="xl"
                        @click="() => leave(close)"
                    >
                        {{ props.leaveLabel }}
                    </UIButton>

                    <slot :close="close" />
                </template>
            </UISheetPage>
        </template>
    </UIDialogStep>
</template>
